import React from 'react';
import PropTypes from 'prop-types';
import { List, Map, fromJS } from 'immutable';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close,
  FilterAltOutlined,
  HighlightOffOutlined,
} from '@mui/icons-material';
import Form from '../../../components/Form';
import FilterRow from './FilterRow';
import getFilters from './filters-utils';
import getPredicates from './predicates-utils';
import { transformFieldName } from './common-utils';

let rowLastUid = 0;

function filterFn(f) {
  return f.get('field') && f.get('operator') && f.get('value');
}

function transformFilters(formState, filters) {
  const state = formState.filter(filterFn);
  const filtersList = getFilters(state, filters);
  const predicates = getPredicates(state, filters);
  return [predicates, filtersList];
}

function FilterForm(props) {
  const {
    id,
    view,
    open,
    save,
    rowsId,
    onClose,
    onFilter,
    setField,
    formState,
    deleteRow,
    setRowsId,
    setOnFilter,
    extraFilters,
    defaultState,
    setFormState,
    cleanForm,
    filters: originalFilters,
    ...others
  } = props;

  const [filters, fieldOptions] = React.useMemo(() => {
    const transformedFilters = fromJS(originalFilters).map((tuple) =>
      tuple.set('field', transformFieldName(tuple.get('field'))),
    );
    const list = List(
      transformedFilters.map((f) =>
        Map({
          key: f.get('field'),
          value: f.get('field'),
          label: f.get('label'),
        }),
      ),
    );
    return [transformedFilters, list];
  }, [originalFilters]);

  React.useEffect(() => {
    /**
     * ? Será que tem alguma forma de verificar se o
     * ? onFilter mudou sem dar loop infinito?
     */
    if (onFilter) {
      setOnFilter(onFilter);
    }
    if (defaultState) {
      const initalFilters = fromJS(defaultState);
      const initialState = Map({ filters: initalFilters });
      setFormState(initialState);
      let newIDs = List([]);
      for (let i = 0; i < initalFilters.size; ++i) {
        rowLastUid += 1;
        newIDs = newIDs.push(rowLastUid);
      }
      setRowsId(newIDs);
      if (onFilter) {
        const [predicates, filtersList] = transformFilters(
          initalFilters,
          filters,
        );
        save(Map({ '!@predicates': predicates, '!@filters': filtersList }));

        if (onFilter && filtersList.size > 0) {
          if (extraFilters.size > 0) {
            onFilter(filtersList.concat(extraFilters));
          } else {
            onFilter(filtersList);
          }
        }
      }
    }
  }, []);

  const rows = React.useMemo(
    () =>
      rowsId.map((uid, index) => {
        const getFilter = (id) => filters.find((f) => f.get('field') === id);
        const currentFilter = getFilter(formState.getIn([index, 'field']));
        return (
          <FilterRow
            index={index}
            rowsId={rowsId}
            key={String(uid)}
            setField={setField}
            setRowsId={setRowsId}
            getFilter={getFilter}
            fieldsList={fieldOptions}
            currentFilter={currentFilter || Map()}
            addRow={() => {
              rowLastUid += 1;
              setRowsId(rowsId.push(rowLastUid));
            }}
            removeRow={(i) => {
              setRowsId(rowsId.delete(i));
              deleteRow(i);
            }}
          />
        );
      }),
    [filters, rowsId, formState],
  );

  const handleApply = (e) => {
    onClose(e);
    const [predicates, filtersList] = transformFilters(formState, filters);
    save(Map({ '!@predicates': predicates, '!@filters': filtersList }));
    if (onFilter && filtersList.size > 0) {
      if (extraFilters.size > 0) {
        onFilter(filtersList.concat(extraFilters));
      } else {
        onFilter(filtersList);
      }
    }
  };

  // Esvazia o storage do filtro salvo + zera o form + reseta as rows.
  // Em seguida dispara onFilter com extraFilters ou null pra recarregar
  // a tabela sem nenhum filtro aplicado. Mantém o drawer aberto pra o
  // usuário poder selecionar novos filtros se quiser.
  const handleClear = (e) => {
    save(Map());
    if (cleanForm) cleanForm();
    setRowsId(List([0]));
    if (onFilter) {
      if (extraFilters && extraFilters.size > 0) {
        onFilter(extraFilters);
      } else {
        onFilter(null);
      }
    }
    onClose(e);
  };

  const hasAppliedFilters = (() => {
    if (!formState || typeof formState.filter !== 'function') return false;
    return formState.filter(filterFn).size > 0;
  })();

  return (
    <Form id={id} view={view}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 480, md: 520 },
              borderTopLeftRadius: { sm: 16 },
              borderBottomLeftRadius: { sm: 16 },
              boxShadow: '-16px 0 48px rgba(0, 30, 45, 0.12)',
              border: 'none',
            },
          },
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 30, 45, 0.35)',
              backdropFilter: 'blur(3px)',
            },
          },
        }}
        {...others}
      >
        {/* HEADER — eyebrow + h1, mesma identidade do navbar/welcome */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: { xs: 2.5, md: 3.5 },
            py: { xs: 2.5, md: 3 },
            borderBottom: '1px solid rgba(0, 128, 173, 0.10)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <FilterAltOutlined sx={{ fontSize: 16, color: '#0080ad' }} />
              <Typography
                sx={{
                  fontFamily: '"Montserrat", system-ui, sans-serif',
                  color: '#0080ad',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '1.4px',
                  textTransform: 'uppercase',
                }}
              >
                Filtros
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontFamily: '"Montserrat", system-ui, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: '#1a3a47',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              Refine sua busca
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", system-ui, sans-serif',
                fontSize: '0.85rem',
                color: 'rgba(96, 125, 139, 0.95)',
                mt: 0.5,
                lineHeight: 1.45,
              }}
            >
              Combine os critérios abaixo para filtrar os resultados.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Fechar filtros"
            sx={{
              color: '#0080ad',
              bgcolor: 'rgba(0, 128, 173, 0.08)',
              borderRadius: '10px',
              p: 0.75,
              '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.14)' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* BODY — lista de rows de filtro */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2.5, md: 3.5 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          {rows}
        </Box>

        {/* FOOTER — Limpar (esquerda, opcional) + Cancelar + Filtrar (direita) */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2.5, md: 3.5 },
            py: 2,
            borderTop: '1px solid rgba(0, 128, 173, 0.10)',
            backgroundColor: '#ffffff',
          }}
        >
          {hasAppliedFilters ? (
            <Button
              onClick={handleClear}
              variant="text"
              startIcon={<HighlightOffOutlined sx={{ fontSize: '18px !important' }} />}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: '#D7372A',
                px: 1.5,
                borderRadius: '10px',
                '&:hover': { bgcolor: 'rgba(215, 55, 42, 0.08)' },
              }}
            >
              Limpar filtros
            </Button>
          ) : (
            <Box />
          )}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={onClose}
              variant="text"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: 'rgba(96, 125, 139, 0.95)',
                px: 2,
                borderRadius: '10px',
                '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.06)' },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              startIcon={<FilterAltOutlined />}
              sx={{
                bgcolor: '#0080ad',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                boxShadow: '0 6px 18px rgba(0, 128, 173, 0.25)',
                '&:hover': {
                  bgcolor: '#197997',
                  boxShadow: '0 8px 22px rgba(0, 128, 173, 0.32)',
                },
              }}
            >
              Filtrar
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Form>
  );
}

FilterForm.defaultProps = {
  id: 'filterForm',
  onFilter: () => null,
  defaultState: null,
};

FilterForm.propTypes = {
  /**
   * identificador do formulário.
   */
  id: PropTypes.string,

  /**
   * View do formulário.
   */
  view: PropTypes.string.isRequired,
  /**
   * Estado de visualização da `Dialog`.
   */
  open: PropTypes.bool.isRequired,
  /**
   * Função a ser chamada para fechar a `Dialog`.
   */
  onClose: PropTypes.func.isRequired,

  /**
   * Callback quando o usuário faz submit do formulário.
   */
  onFilter: PropTypes.func,

  /**
   * Filtros padrões do formulário.
   */
  defaultState: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      operator: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      value: PropTypes.any,
    }),
  ),

  /**
   * Propriedades de cada filtro.
   */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
        .isRequired, // campo em que se encontra o valor
      label: PropTypes.string.isRequired,
      condition: PropTypes.func, // condição de filtragem (apenas predicados)
      view: PropTypes.string,
      type: PropTypes.string,
      reload: PropTypes.bool, // Se o filtro deve ou não chamar o back
      options: PropTypes.instanceOf(List),
    }),
  ).isRequired,

  // * Props de estado
  rowsId: PropTypes.instanceOf(List).isRequired,
  setRowsId: PropTypes.func.isRequired,
  formState: PropTypes.instanceOf(List).isRequired,
  deleteRow: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  setField: PropTypes.func.isRequired,
};

export default FilterForm;
